import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../data/blogData';
import ShinyText from '../ui/reactbits/ShinyText';
import ScrollToTop from '../utils/ScrollToTop';

const BlogPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Get the latest blog post (first in array)
  const latestPost = blogPosts[0];
  
  // Filter and search blog posts
  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return blogPosts;
    
    const query = searchQuery.toLowerCase();
    return blogPosts.filter(post =>
      post.title.toLowerCase().includes(query) ||
      post.excerpt.toLowerCase().includes(query) ||
      post.category.toLowerCase().includes(query) ||
      post.content.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Separate latest from other posts for display
  const otherPosts = filteredPosts.filter(post => post.id !== latestPost.id);

  return (
    <>
      <ScrollToTop />
      <div className="animate-on-scroll">
      {/* Hero Section */}
      <section className="relative z-10 py-20 md:py-24 bg-secondary">
        <div className="container relative mx-auto px-3 sm:px-4 lg:px-6 animate-on-scroll">
          <div className="max-w-4xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              Our Blog
            </h1>
            <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mb-8">
              Insights, tips, and industry knowledge to help you build better digital experiences.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-md">
              <div 
                className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <svg
                  className={`h-5 w-5 transition-all duration-300 ${
                    isSearchFocused 
                      ? 'text-black' 
                      : 'text-foreground/40'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={isSearchFocused ? "2.5" : "2"}
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    transform: isSearchFocused ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search blog posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className={`relative block w-full pl-10 pr-3 py-3 border rounded-lg bg-background/50 backdrop-blur-sm text-foreground placeholder-foreground/50 focus:outline-none transition-all duration-300 ${
                  isSearchFocused 
                    ? 'border-primary ring-2 ring-primary/30 glow-card' 
                    : 'border-foreground/20'
                }`}
                style={{
                  boxShadow: isSearchFocused ? '0 0 30px rgba(6, 182, 212, 0.25), 0 0 60px rgba(6, 182, 212, 0.15)' : 'none'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Latest Post */}
      {!searchQuery && (
        <section className="relative py-16 md:py-20">
          <div className="container relative mx-auto px-3 sm:px-4 lg:px-6 animate-on-scroll" data-delay="0.1s">
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-2">Featured Post</h2>
              <div className="w-16 h-1 bg-primary rounded-full"></div>
            </div>
            
            <article className="max-w-4xl">
              <div className="group glow-card bg-background border-2 border-foreground/10 rounded-lg p-8 md:p-12 transition-all duration-300 hover:border-foreground/20 hover:scale-[1.02]">
                {/* Category */}
                <div className="mb-4">
                  <span className="inline-block bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-3 py-1 rounded">
                    {latestPost.category}
                  </span>
                </div>
                
                {/* Title */}
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold leading-tight mb-4">
                  <Link 
                    to={`/blog/${latestPost.categorySlug}/${latestPost.slug}`}
                    className="inline-block"
                  >
                    <ShinyText text={latestPost.title} speed={2.5} className="text-foreground" />
                  </Link>
                </h3>
                
                {/* Meta */}
                <div className="flex items-center space-x-4 text-foreground/60 font-mono text-sm mb-6">
                  <span>{latestPost.date}</span>
                  <span>•</span>
                  <span>{latestPost.readTime}</span>
                </div>
                
                {/* Excerpt */}
                <p className="text-foreground/80 leading-relaxed mb-6 text-lg">
                  {latestPost.excerpt}
                </p>
                
                {/* Read More Link */}
                <Link 
                  to={`/blog/${latestPost.categorySlug}/${latestPost.slug}`}
                  className="inline-flex items-center font-medium transition-all duration-300 group"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #06b6d4 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    backgroundSize: '200% 100%',
                    backgroundPosition: '0% 50%',
                    transition: 'background-position 0.5s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundPosition = '100% 50%';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundPosition = '0% 50%';
                  }}
                >
                  Read Full Article
                  <svg 
                    className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    style={{ stroke: '#06b6d4' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* All Posts Grid */}
      <section className="relative py-16 md:py-20 border-t border-foreground/10">
        <div className="container relative mx-auto px-3 sm:px-4 lg:px-6 animate-on-scroll" data-delay="0.2s">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-2">
              {searchQuery ? `Search Results (${filteredPosts.length})` : 'All Posts'}
            </h2>
            <div className="w-16 h-1 bg-primary rounded-full"></div>
          </div>
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-foreground/60 text-lg">
                No blog posts found matching "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="grid gap-8 md:gap-12">
              {(searchQuery ? filteredPosts : otherPosts).map((post, index) => (
                <article 
                  key={post.id} 
                  className="group animate-on-scroll"
                  data-delay={`${Math.min(index * 0.1, 0.3)}s`}
                >
                  <div className="glow-card bg-background border border-foreground/10 rounded-lg p-6 md:p-8 transition-all duration-300 hover:border-foreground/20 hover:scale-[1.01]">
                    <div className="flex flex-col md:flex-row md:items-start md:space-x-8">
                      <div className="flex-1">
                        {/* Category */}
                        <div className="mb-3">
                          <span className="inline-block bg-secondary text-foreground font-mono text-xs uppercase tracking-widest px-2 py-1 rounded">
                            {post.category}
                          </span>
                        </div>
                        
                        {/* Title */}
                        <h3 className="text-xl md:text-2xl font-display font-bold leading-tight mb-3">
                          <Link 
                            to={`/blog/${post.categorySlug}/${post.slug}`}
                            className="inline-block"
                          >
                            <ShinyText text={post.title} speed={2.5} className="text-foreground" />
                          </Link>
                        </h3>
                        
                        {/* Meta */}
                        <div className="flex items-center space-x-4 text-foreground/60 font-mono text-sm mb-4">
                          <span>{post.date}</span>
                          <span>•</span>
                          <span>{post.readTime}</span>
                        </div>
                        
                        {/* Excerpt */}
                        <p className="text-foreground/80 leading-relaxed mb-4">
                          {post.excerpt}
                        </p>
                        
                        {/* Read More Link */}
                        <Link 
                          to={`/blog/${post.categorySlug}/${post.slug}`}
                          className="inline-flex items-center font-medium transition-all duration-300 group"
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #06b6d4 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                            backgroundSize: '200% 100%',
                            backgroundPosition: '0% 50%',
                            transition: 'background-position 0.5s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundPosition = '100% 50%';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundPosition = '0% 50%';
                          }}
                        >
                          Read More
                          <svg 
                            className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            style={{ stroke: '#06b6d4' }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      </div>
    </>
  );
};

export default BlogPageContent;
